import { SHStack, SHTypography } from "@components/design-systems";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import {
  AnalysisSummaryDTO,
  RenderAnalysisSummaryItemProps,
} from "@models/platform-analysis/entities/steps/summary";
import { TableViewMode } from "@models/platform-analysis/enums/tableViewMode";
import {
  sortByProductOrder,
  sortFeatureAnalysisRowsByProductOrder,
} from "@pages/platform-analysis-v2/_id/utils";
import { summaryFeatureAnalysisShortlistedTableRowsSelectors as rowSelectors } from "@redux/slices/platform-analysis";
import { RootState } from "@redux/store";
import { isEqual } from "lodash";
import { memo, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { AnalysisSummaryStaticValueConfigs } from "../../configs";
import { SummaryAnalysisTable } from "./table-components/container";
import { SummaryFeatureAnalysisTableToolbar } from "./toolbar";

export const SummaryFeatureAnalysis = memo(
  ({
    isReview,
    disabled: sectionDisabled,
    name,
    userType,
  }: RenderAnalysisSummaryItemProps) => {
    const { setValue } = useFormContext<AnalysisSummaryDTO>();
    const { setIsShowAnalysisScores, setSummaryFeatureAnalysisTableViewMode } =
      usePlatformAnalysis();

    const storedViewMode = useSelector(
      (state: RootState) =>
        state.platformAnalysis?.platformAnalysis?.summary
          ?.featureAnalysisTableViewMode,
    );
    const storedShowAnalysisScore = useSelector(
      (state: RootState) =>
        state.platformAnalysis?.platformAnalysis?.summary?.showAnalysisScore,
    );
    const storedNumOfFeaturesSelected = useSelector(
      (state: RootState) =>
        state.platformAnalysis?.platformAnalysis?.summary?.featureAnalysis
          ?.shortlisted?.overallScore?.totalSelectedFeature || 0,
    );
    const storedSelectedProducts = useSelector(
      (state: RootState) =>
        state.platformAnalysis?.platformAnalysis?.summary?.featureAnalysis
          ?.shortlisted?.selectedProducts || [],
    );
    const storedTableRows = useSelector(rowSelectors.selectAll);
    const storedCurrentProductRecommendations = useSelector(
      (state: RootState) =>
        state.platformAnalysis?.internal?.summary?.productRecommendations,
    );

    const [viewMode, setViewMode] = useState<TableViewMode>(
      storedViewMode || TableViewMode.Default,
    );
    const [showAnalysisScore, setShowAnalysisScore] = useState<boolean>(
      !!storedShowAnalysisScore,
    );
    const [productIdsOrder, setProductIdsOrder] = useState<string[]>(
      Object.keys(storedCurrentProductRecommendations || {}),
    );

    const handleOnViewModeChange = (mode: TableViewMode) => {
      setValue("featureAnalysisTableViewMode", mode);
      setViewMode(mode);
      setSummaryFeatureAnalysisTableViewMode(mode);
    };

    const handleOnShowAnalysisScoreChange = (show: boolean) => {
      setValue("showAnalysisScore", show);
      setShowAnalysisScore(show);
      setIsShowAnalysisScores(show);
    };

    const disableDetailView = useMemo(
      () =>
        storedNumOfFeaturesSelected >
        AnalysisSummaryStaticValueConfigs.MaximumFeaturesInDetailView,
      [storedNumOfFeaturesSelected],
    );

    const shouldRemoveFeatureRows = useMemo(() => {
      return disableDetailView || viewMode !== TableViewMode.Detail;
    }, [viewMode, disableDetailView]);

    const shouldRemoveSubSectionRows = useMemo(() => {
      return viewMode === TableViewMode.Summary;
    }, [viewMode]);

    const orderedSelectedProducts = useMemo(() => {
      if (!isReview) return storedSelectedProducts;

      const sortedData = sortByProductOrder({
        data: storedSelectedProducts,
        productRecommendations: storedCurrentProductRecommendations,
        getItemId: (item) => item.id,
      });

      const newIds = sortedData.map((item) => item.id);
      if (!isEqual(productIdsOrder, newIds)) setProductIdsOrder(newIds);

      return sortedData;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReview, storedSelectedProducts, storedCurrentProductRecommendations]);

    const orderedRows = useMemo(() => {
      if (!isReview) return storedTableRows;

      return sortFeatureAnalysisRowsByProductOrder({
        originalRows: storedTableRows,
        productRecommendations: storedCurrentProductRecommendations,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storedTableRows, productIdsOrder, isReview]);

    return (
      <SHStack gap={2}>
        <SHStack
          width={"100%"}
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <SHStack>
            <SHTypography disabled={sectionDisabled} variant="subtitle2">
              {name?.[userType]}
            </SHTypography>
          </SHStack>

          <SummaryFeatureAnalysisTableToolbar
            disableDetailView={disableDetailView}
            disabled={sectionDisabled}
            onViewModeChange={handleOnViewModeChange}
            onShowAnalysisScoreChange={handleOnShowAnalysisScoreChange}
          />
        </SHStack>

        <SummaryAnalysisTable
          isReview={isReview}
          rows={orderedRows}
          selectedProducts={orderedSelectedProducts}
          removeFeatureLevelRows={shouldRemoveFeatureRows}
          removeSubSectionLevelRows={shouldRemoveSubSectionRows}
          wrapperProps={{
            showAnalysisScore: showAnalysisScore,
            viewMode: viewMode,
          }}
        />
      </SHStack>
    );
  },
);
